import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { clearDeviceToken } from '../../lib/deviceTrust';

interface IdleLockScreenProps {
  onUnlock: () => void;
}

const IdleLockScreen: React.FC<IdleLockScreenProps> = ({ onUnlock }) => {
  const { user, profile } = useAuth();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const displayName = profile?.full_name || 'User';
  const email = user?.email || '';
  const maskedEmail = email.length > 4
    ? email[0] + '••••' + email.slice(email.indexOf('@'))
    : email;

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        setError('Incorrect password. Please try again.');
      } else {
        setPassword('');
        onUnlock();
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    clearDeviceToken();
    await supabase.auth.signOut();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0D1827]">
      <div className="w-full max-w-sm mx-4 bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-blue/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-accent-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-[#0D1827]">Session Locked</h2>
          <p className="mt-1 text-sm text-text-muted">{displayName}</p>
          <p className="text-sm text-text-muted">{maskedEmail}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 text-sm text-status-danger bg-status-danger-light border border-status-danger/20 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleUnlock} className="space-y-4">
          <div>
            <label htmlFor="lock-password" className="block text-sm font-medium text-text-body mb-1.5">
              Password
            </label>
            <input
              id="lock-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="block w-full px-4 py-3 font-sans text-base placeholder-text-muted border border-surface-border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-accent-blue-wash focus:border-accent-blue transition-colors"
              placeholder="Enter your password"
              disabled={loading}
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center w-full px-4 py-3 min-h-[44px] text-base font-semibold text-white bg-[#2563EB] border border-transparent rounded-lg shadow-sm hover:bg-accent-blue-hover hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/25 active:translate-y-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-blue disabled:opacity-50 transition-all duration-200"
          >
            {loading ? 'Unlocking...' : 'Unlock'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={handleSignOut}
            className="text-sm font-medium text-text-muted hover:text-status-danger hover:underline transition-colors"
          >
            Sign out completely
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleLockScreen;
